import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { classNames } from "primereact/utils";
import { useLocation } from "react-router-dom";
import { useMountEffect } from "primereact/hooks";
import PrimeReact from "primereact/api";
import { useTranslation } from "react-i18next";
import { get } from "lodash";
import { SpeedDial } from "primereact/speeddial";
import { Tooltip } from "primereact/tooltip";
import AppTopbar from "./AppTopbar";
import AppProfileSidebar from "./AppProfileSidebar";
import AppConfig from "./AppConfig";
import ChatWindow from "./ChatWindow";
import TodoWindow from "./TodoWindow";
import AppSidebar from "../AppSidebar/AppSidebar";
import { LayoutContext } from "../../contexts/LayoutContext";
import { useSidebar } from "../../contexts/SidebarContext";
import Auth from "../../utils/Auth";
import { AppTopbarRef } from "../../types";

const AppolloLayout = (props: { children: React.ReactNode }) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const { layoutConfig } = useContext(LayoutContext);
    const {
        isMini,
        isExpanded,
        isMobile,
        mobileOpen,
        closeMobile,
    } = useSidebar();
    const [chatWindow, setChatWindow] = useState<boolean>(false);
    const [todoWindow, setTodoWindow] = useState<boolean>(false);
    const topbarRef = useRef<AppTopbarRef>(null);
    const auth = new Auth();

    useMountEffect(() => {
        PrimeReact.ripple = true;
    });

    useEffect(() => {
        closeMobile();
    }, [location.pathname, closeMobile]);

    useEffect(() => {
        const lang = get(auth.getUser(), ["app_settings", "lang"]) || "en";
        i18n.changeLanguage(lang);
    }, [i18n]);

    const blockBodyScroll = useCallback(() => {
        document.body.classList.add("blocked-scroll");
    }, []);

    const unblockBodyScroll = useCallback(() => {
        document.body.classList.remove("blocked-scroll");
    }, []);

    useEffect(() => {
        if (isMobile && mobileOpen) {
            blockBodyScroll();
            return () => unblockBodyScroll();
        }

        unblockBodyScroll();
    }, [blockBodyScroll, isMobile, mobileOpen, unblockBodyScroll]);

    const containerClass = classNames("app-layout", {
        "app-layout--expanded": isExpanded,
        "app-layout--mini": isMini,
        "app-layout--mobile": isMobile,
        "app-layout--mobile-open": isMobile && mobileOpen,
        "layout-light": layoutConfig.colorScheme === "light",
        "layout-dim": layoutConfig.colorScheme === "dim",
        "layout-dark": layoutConfig.colorScheme === "dark",
        "p-input-filled": layoutConfig.inputStyle === "filled",
        "p-ripple-disabled": !layoutConfig.ripple,
    });

    const speedDialItems = [
        {
            label: t("chat"),
            icon: "fas fa-comments",
            command: () => {
                setChatWindow(true);
            },
        },
        {
            label: t("to-do manage"),
            icon: "fas fa-check-square",
            command: () => {
                setTodoWindow(true);
            },
        },
    ];

    return (
        <div className={containerClass}>
            <AppSidebar />

            <div className="app-layout__main">
                <AppTopbar ref={topbarRef} />
                <div className="app-layout__content layout-content">
                    {props.children}
                </div>
            </div>

            <AppProfileSidebar />
            <AppConfig />

            <Tooltip
                target=".speeddial-bottom-left .p-speeddial-action"
                position="left"
            />
            <SpeedDial
                model={speedDialItems}
                direction="up"
                transitionDelay={80}
                showIcon="pi pi-plus"
                hideIcon="pi pi-times"
                className="speeddial-bottom-left helper-speed-dial"
                buttonClassName="bg-primary p-button-help p-button-outlined"
            />

            <ChatWindow
                visible={chatWindow}
                onClose={() => setChatWindow(false)}
            />
            <TodoWindow
                visible={todoWindow}
                onClose={() => setTodoWindow(false)}
            />

            <div
                className="app-layout__mask layout-mask"
                onClick={closeMobile}
                role="presentation"
            />
        </div>
    );
};

export default AppolloLayout;
